import React, { useState, useEffect, useContext, useRef } from 'react';
import AppContext from '../../contexts/AppContext';
import { MessageCircle, X, Send, Shield, Users, CheckCircle, Plus, LogOut, Image, UserX, Trash2, Crown, Edit, Lock } from 'lucide-react';
import * as firestoreService from '../../services/firestoreService';
import { uploadMessageMedia } from '../../services/storageService';
import SecurityUtils from '../../utils/security';
import { validateVideoDuration, fixVideoOrientation } from '../../utils/media';
import Avatar from '../../components/common/Avatar';

const GroupDetailView = ({ groupId }) => {
  const { groups, setGroups, users, currentUser, setView, conversations, setConversations, messages, setMessages, showAlert, showConfirm } = useContext(AppContext);
  const [showInvite, setShowInvite] = useState(false);
  const [showEditGroup, setShowEditGroup] = useState(false);
  const [editGroupForm, setEditGroupForm] = useState({ name: '', description: '', isPrivate: false });
  const [savingGroupEdit, setSavingGroupEdit] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [joinRequested, setJoinRequested] = useState(false);
  const [fetchedGroup, setFetchedGroup] = useState(null);
  const [activeTab, setActiveTab] = useState('chat');
  const [newMessage, setNewMessage] = useState('');
  const [grpMediaFile, setGrpMediaFile] = useState(null);
  const [grpMediaPreview, setGrpMediaPreview] = useState(null);
  const [grpMediaType, setGrpMediaType] = useState(null);
  const [grpSendingMedia, setGrpSendingMedia] = useState(false);
  const [grpViewMedia, setGrpViewMedia] = useState(null);
  const grpMediaInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const grpMessagesContainerRef = useRef(null);
  const localGroup = groups.find(g => g.id === groupId);
  const group = localGroup || fetchedGroup;

  useEffect(() => {
    if (!localGroup) {
      firestoreService.getGroup(groupId).then(g => {
        if (g) setFetchedGroup(g);
      }).catch(() => {});
    }
  }, [groupId, localGroup]);

  // Auto-fix orphaned groups: re-add creator if missing from members
  useEffect(() => {
    if (group && group.createdBy && !group.members?.includes(group.createdBy)) {
      firestoreService.addGroupMember(group.id, group.createdBy).catch(err =>
        console.error('Error re-adding group creator:', err)
      );
    }
  }, [group]);

  // Real-time messages listener for group chat
  useEffect(() => {
    if (!group?.groupChatId || !group.members?.includes(currentUser.id)) return;
    const unsub = firestoreService.onMessagesChange(group.groupChatId, (convMsgs) => {
      setMessages(prev => {
        const otherMsgs = prev.filter(m => m.conversationId !== group.groupChatId);
        return [...otherMsgs, ...convMsgs];
      });
      firestoreService.markConversationAsRead(group.groupChatId, currentUser.id).catch(() => {});
    });
    firestoreService.markConversationAsRead(group.groupChatId, currentUser.id).catch(() => {});
    return () => unsub();
  }, [group?.groupChatId, group?.members, currentUser.id, setMessages]);

  const groupMessages = messages.filter(m => m.conversationId === group?.groupChatId) || [];

  // Scroll to bottom when entering chat tab or when messages change
  useEffect(() => {
    if (activeTab === 'chat' && groupMessages.length > 0) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 100);
    }
  }, [activeTab, groupMessages.length]);

  if (!group) {
    return (
      <div className="max-w-2xl mx-auto p-4 text-center">
        <p className="text-white">Group not found.</p>
        <button onClick={() => setView('my-groups')} className="mt-4 px-4 py-2 bg-[#16a34a] text-pure-white rounded-lg">Back to My Groups</button>
      </div>
    );
  }

  const memberUsers = group.members?.map(id => users.find(u => u.id === id)).filter(Boolean) || [];
  const isOwner = group.createdBy === currentUser.id;
  const isModerator = group.moderators?.includes(currentUser.id);
  const isOwnerOrModerator = isOwner || isModerator;
  const isMember = group.members?.includes(currentUser.id);
  const isPendingInvite = group.pendingInvites?.includes(currentUser.id);
  const isPendingJoinRequest = group.joinRequests?.includes(currentUser.id);

  // Users available to invite — must be followers/following, not already in group or pending
  const invitableUsers = users.filter(u =>
    u.id !== currentUser.id &&
    (currentUser.following?.includes(u.id) || u.following?.includes(currentUser.id)) &&
    !group.members?.includes(u.id) &&
    !group.pendingInvites?.includes(u.id) &&
    !group.joinRequests?.includes(u.id)
  );

  const handleGrpMediaSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const isVideo = file.type.startsWith('video/');
    const validation = SecurityUtils.validateFile(file, { maxSize: isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024 });
    if (!validation.valid) { showAlert(`File validation failed: ${validation.error}`, 'Error'); return; }
    if (validation.type !== 'image' && validation.type !== 'video') { showAlert('Please select an image or video file.', 'Error'); return; }
    if (isVideo) {
      const validDuration = await validateVideoDuration(file, 15);
      if (!validDuration) { showAlert('Video must be 15 seconds or shorter.', 'Error'); return; }
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setGrpMediaFile(file);
      setGrpMediaPreview(reader.result);
      setGrpMediaType(isVideo ? 'video' : 'image');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const clearGrpMedia = () => {
    setGrpMediaFile(null);
    setGrpMediaPreview(null);
    setGrpMediaType(null);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !grpMediaFile) return;
    if (!group.groupChatId) return;
    const sanitizedMessage = newMessage.trim() ? SecurityUtils.sanitizeText(newMessage) : '';
    setNewMessage('');
    const currentMediaFile = grpMediaFile;
    const currentMediaType = grpMediaType;
    clearGrpMedia();
    try {
      setGrpSendingMedia(!!currentMediaFile);
      let mediaUrl = null;
      if (currentMediaFile) {
        const fileToUpload = currentMediaType === 'video' ? await fixVideoOrientation(currentMediaFile) : currentMediaFile;
        const result = await uploadMessageMedia(fileToUpload, currentUser.id, group.groupChatId);
        mediaUrl = result.url;
      }
      await firestoreService.sendMessage({
        conversationId: group.groupChatId,
        senderId: currentUser.id,
        content: sanitizedMessage,
        ...(mediaUrl ? { mediaUrl, mediaType: currentMediaType } : {}),
      });
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setGrpSendingMedia(false);
    }
  };

  const handleAcceptInvite = async () => {
    try {
      await firestoreService.acceptGroupInvite(groupId, currentUser.id);
    } catch (err) {
      console.error('Error accepting group invite:', err);
    }
  };

  const handleDeclineInvite = async () => {
    try {
      await firestoreService.declineGroupInvite(groupId, currentUser.id);
      setView('my-groups');
    } catch (err) {
      console.error('Error declining group invite:', err);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (userId === group.createdBy) return;
    if (isModerator && !isOwner && group.moderators?.includes(userId)) {
      showAlert('Moderators cannot remove other moderators', 'Error');
      return;
    }
    const memberUser = users.find(u => u.id === userId);
    if (!await showConfirm(`Remove ${memberUser?.name || 'this user'} from the group?`, 'Remove Member', 'Remove', true)) return;
    try {
      await firestoreService.removeGroupMember(groupId, userId);
    } catch (err) {
      console.error('Error removing member:', err);
    }
  };

  const handleAddModerator = async (userId) => {
    const memberUser = users.find(u => u.id === userId);
    if (!await showConfirm(`Make ${memberUser?.name || 'this user'} a moderator?`, 'Add Moderator', 'Confirm')) return;
    try {
      await firestoreService.addGroupModerator(groupId, userId);
      firestoreService.createNotification({
        targetUserId: userId,
        fromUserId: currentUser.id,
        type: 'group_invite',
        message: `made you a moderator of "${group.name}"`,
        groupId: groupId,
      }).catch(err => console.error('Error creating notification:', err));
    } catch (err) {
      console.error('Error adding moderator:', err);
    }
  };

  const handleRemoveModerator = async (userId) => {
    const memberUser = users.find(u => u.id === userId);
    if (!await showConfirm(`Remove ${memberUser?.name || 'this user'} from moderators?`, 'Remove Moderator', 'Remove', true)) return;
    try {
      await firestoreService.removeGroupModerator(groupId, userId);
      firestoreService.createNotification({
        targetUserId: userId,
        fromUserId: currentUser.id,
        type: 'group_invite',
        message: `removed you as moderator of "${group.name}"`,
        groupId: groupId,
      }).catch(err => console.error('Error creating notification:', err));
    } catch (err) {
      console.error('Error removing moderator:', err);
    }
  };

  const handleRequestToJoin = async () => {
    setJoinRequested(true);
    try {
      await firestoreService.requestToJoinGroup(groupId, currentUser.id);
      setGroups(prev => prev.map(g => g.id === groupId ? { ...g, joinRequests: [...(g.joinRequests || []), currentUser.id] } : g));
      firestoreService.createNotification({
        targetUserId: group.createdBy,
        fromUserId: currentUser.id,
        type: 'group_join_request',
        message: `requested to join your group "${group.name}"`,
        groupId: groupId,
      }).catch(err => console.error('Error creating join request notification:', err));
    } catch (err) {
      console.error('Error requesting to join group:', err);
      setJoinRequested(false);
    }
  };

  const handleAcceptJoinRequest = async (userId) => {
    try {
      await firestoreService.acceptJoinRequest(groupId, userId);
      firestoreService.createNotification({
        targetUserId: userId,
        fromUserId: currentUser.id,
        type: 'group_invite',
        message: `accepted your request to join "${group.name}"`,
        groupId: groupId,
      }).catch(err => console.error('Error creating notification:', err));
    } catch (err) {
      console.error('Error accepting join request:', err);
    }
  };

  const handleDeclineJoinRequest = async (userId) => {
    try {
      await firestoreService.declineJoinRequest(groupId, userId);
    } catch (err) {
      console.error('Error declining join request:', err);
    }
  };

  const handleOpenEditGroup = () => {
    setEditGroupForm({ name: group.name, description: group.description || '', isPrivate: group.isPrivate || false });
    setShowEditGroup(true);
  };

  const handleSaveEditGroup = async () => {
    if (!editGroupForm.name.trim()) return;
    setSavingGroupEdit(true);
    try {
      const updates = {
        name: SecurityUtils.sanitizeText(editGroupForm.name.trim()),
        description: SecurityUtils.sanitizeText(editGroupForm.description),
        isPrivate: editGroupForm.isPrivate,
      };
      await firestoreService.updateGroup(groupId, updates);
      setGroups(prev => prev.map(g => g.id === groupId ? { ...g, ...updates } : g));
      setShowEditGroup(false);
    } catch (err) {
      console.error('Error updating group:', err);
      showAlert('Failed to update group.', 'Error');
    }
    setSavingGroupEdit(false);
  };

  const handleInviteMembers = async () => {
    try {
      for (const userId of selectedMembers) {
        await firestoreService.inviteToGroup(groupId, userId);
        firestoreService.createNotification({
          targetUserId: userId,
          fromUserId: currentUser.id,
          type: 'group_invite',
          message: `invited you to join the group "${group.name}"`,
          groupId: groupId,
        }).catch(err => console.error('Error creating group invite notification:', err));
      }
      setSelectedMembers([]);
      setShowInvite(false);
    } catch (err) {
      console.error('Error inviting members:', err);
    }
  };

  const handleLeaveGroup = async () => {
    if (!await showConfirm('Are you sure you want to leave this group?', 'Leave Group', 'Leave', true)) return;
    try {
      await firestoreService.removeGroupMember(groupId, currentUser.id);
      setView('my-groups');
    } catch (err) {
      console.error('Error leaving group:', err);
    }
  };

  const handleDeleteGroup = async () => {
    if (!await showConfirm('Are you sure you want to delete this group? This cannot be undone.', 'Delete Group', 'Delete', true)) return;
    try {
      await firestoreService.deleteGroup(groupId);
      setView('my-groups');
    } catch (err) {
      console.error('Error deleting group:', err);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4">
      <button onClick={() => setView('my-groups')} className="text-[#16a34a] hover:underline mb-4 inline-block">&larr; Back to My Groups</button>

      {isPendingInvite && (
        <div className="bg-[#16a34a] bg-opacity-10 border border-[#16a34a] rounded-lg p-4 mb-4 flex items-center justify-between">
          <p className="text-white font-medium">You've been invited to join this group!</p>
          <div className="flex space-x-2">
            <button onClick={handleAcceptInvite} className="px-4 py-2 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d] font-medium">Accept</button>
            <button onClick={handleDeclineInvite} className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 font-medium">Decline</button>
          </div>
        </div>
      )}

      {!isMember && !isPendingInvite && (
        <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 mb-4 flex items-center justify-between">
          {isPendingJoinRequest || joinRequested ? (
            <p className="text-yellow-400 font-medium">Your request to join was sent</p>
          ) : (
            <>
              <p className="text-white font-medium">Want to join this group?</p>
              <button onClick={handleRequestToJoin} className="px-4 py-2 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d] font-medium">Request to Join</button>
            </>
          )}
        </div>
      )}

      <div className="bg-black rounded-lg shadow border border-gray-700 p-6">
        <div className="flex items-start justify-between mb-1">
          <div className="flex items-center space-x-2 flex-wrap gap-y-1">
            <h2 className="text-2xl font-bold text-white">{group.name}</h2>
            {group.isPrivate && (
              <span className="flex items-center space-x-1 text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded-full">
                <Lock className="w-3 h-3" /><span>Private</span>
              </span>
            )}
          </div>
          {isOwner && (
            <button onClick={handleOpenEditGroup} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg flex-shrink-0" title="Edit group">
              <Edit className="w-4 h-4" />
            </button>
          )}
        </div>
        {group.description && <p className="text-gray-400 mb-4">{group.description}</p>}

        {/* Private group — non-member sees only the name above, nothing else */}
        {group.isPrivate && !isMember && !isPendingInvite && (
          <p className="text-gray-500 text-sm">This is a private group. Only members can see its content.</p>
        )}

        {isMember && (
          <div className="flex space-x-2 mb-4 flex-wrap gap-y-2">
            {isOwnerOrModerator && (
              <button onClick={() => setShowInvite(true)} className="flex items-center space-x-2 px-3 py-2 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d]">
                <Plus className="w-4 h-4" />
                <span>Invite</span>
              </button>
            )}
            {!isOwner && (
              <button onClick={handleLeaveGroup} className="flex items-center space-x-2 px-3 py-2 bg-red-600 text-pure-white rounded-lg hover:bg-red-700">
                <LogOut className="w-4 h-4" />
                <span>Leave</span>
              </button>
            )}
            {isOwner && (
              <button onClick={handleDeleteGroup} className="flex items-center space-x-2 px-3 py-2 bg-red-800 text-pure-white rounded-lg hover:bg-red-900">
                <Trash2 className="w-4 h-4" />
                <span>Delete</span>
              </button>
            )}
          </div>
        )}

        {/* Tab Navigation — members only */}
        {isMember && (
          <>
            <div className="flex space-x-1 mb-4 bg-gray-900 rounded-lg p-1">
              <button onClick={() => setActiveTab('chat')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${activeTab === 'chat' ? 'bg-[#16a34a] text-pure-white' : 'text-gray-400 hover:text-white'}`}>
                <span className="flex items-center justify-center space-x-1"><MessageCircle className="w-4 h-4" /><span>Chat</span></span>
              </button>
              <button onClick={() => setActiveTab('members')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${activeTab === 'members' ? 'bg-[#16a34a] text-pure-white' : 'text-gray-400 hover:text-white'}`}>
                <span className="flex items-center justify-center space-x-1"><Users className="w-4 h-4" /><span>Members ({memberUsers.length})</span></span>
              </button>
            </div>

            {/* Chat Tab */}
            {activeTab === 'chat' && (
              <div className="border border-gray-700 rounded-lg overflow-hidden">
                <div ref={grpMessagesContainerRef} className="h-[400px] overflow-y-auto p-4 space-y-3 bg-[rgb(var(--color-bg-primary))]">
                  {groupMessages.length === 0 && (
                    <div className="text-center text-gray-500 py-8">
                      <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No messages yet. Start the conversation!</p>
                    </div>
                  )}
                  {groupMessages.map(msg => {
                    if (msg.senderId === 'system') {
                      return (
                        <div key={msg.id} className="flex justify-center my-2">
                          <p className="text-xs text-gray-400 bg-gray-800/50 px-3 py-1 rounded-full italic">{msg.content}</p>
                        </div>
                      );
                    }
                    const isMine = msg.senderId === currentUser.id;
                    const sender = users.find(u => u.id === msg.senderId);
                    return (
                      <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                        {!isMine && <div className="flex-shrink-0 self-start mt-1 mr-1"><Avatar user={sender} size="xs" /></div>}
                        <div className={`max-w-[75%] ${isMine ? 'bg-[#16a34a] text-pure-white' : 'bg-[rgb(var(--color-bg-tertiary))] text-[rgb(var(--color-text-primary))]'} rounded-2xl px-3 py-2`}>
                          {!isMine && sender && (
                            <p className="text-xs font-bold mb-1 opacity-80 flex items-center">
                              {sender.name}
                              {msg.senderId === group.createdBy && <Crown className="w-3 h-3 ml-1 text-yellow-300" title="Group Owner" />}
                              {msg.senderId !== group.createdBy && group.moderators?.includes(msg.senderId) && <Shield className="w-3 h-3 ml-1 text-blue-300" title="Moderator" />}
                            </p>
                          )}
                          {isMine && (msg.senderId === group.createdBy || group.moderators?.includes(msg.senderId)) && (
                            <p className="text-xs mb-1 opacity-70 flex items-center justify-end">
                              {msg.senderId === group.createdBy && <Crown className="w-3 h-3 text-yellow-300" title="Group Owner" />}
                              {group.moderators?.includes(msg.senderId) && msg.senderId !== group.createdBy && <Shield className="w-3 h-3 text-blue-300" title="Moderator" />}
                            </p>
                          )}
                          {msg.mediaUrl && msg.mediaType === 'image' && (
                            <img src={msg.mediaUrl} alt="" className="rounded-lg max-w-full max-h-60 mb-1 cursor-pointer" onClick={() => setGrpViewMedia({ url: msg.mediaUrl, type: 'image' })} />
                          )}
                          {msg.mediaUrl && msg.mediaType === 'video' && (
                            <video src={msg.mediaUrl} controls playsInline className="rounded-lg max-w-full max-h-60 mb-1 cursor-pointer" onClick={(e) => { e.preventDefault(); setGrpViewMedia({ url: msg.mediaUrl, type: 'video' }); }} />
                          )}
                          {msg.content && <p className="text-sm break-words">{msg.content}</p>}
                          <p className="text-xs mt-1 opacity-70">{new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                        {isMine && <div className="flex-shrink-0 self-start mt-1 ml-1"><Avatar user={currentUser} size="xs" /></div>}
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
                <div className="p-3 border-t border-gray-700 bg-black overflow-hidden">
                  {grpMediaPreview && (
                    <div className="mb-2 relative inline-block">
                      {grpMediaType === 'image' ? (
                        <img src={grpMediaPreview} alt="Preview" className="max-h-32 rounded-lg" />
                      ) : (
                        <video src={grpMediaPreview} className="max-h-32 rounded-lg" controls playsInline />
                      )}
                      <button onClick={clearGrpMedia} className="absolute -top-2 -right-2 bg-red-600 text-pure-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-700">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  {grpSendingMedia && (
                    <p className="text-xs text-gray-400 mb-1">Uploading media...</p>
                  )}
                  <div className="flex space-x-2">
                    <button onClick={() => grpMediaInputRef.current?.click()} className="p-3 text-gray-400 hover:text-white flex-shrink-0" title="Attach photo or video">
                      <Image className="w-5 h-5" />
                    </button>
                    <input ref={grpMediaInputRef} type="file" accept="image/*,video/*" onChange={handleGrpMediaSelect} className="hidden" />
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && !grpSendingMedia && handleSendMessage()}
                      placeholder="Type a message..."
                      className="flex-1 min-w-0 px-4 py-3 border border-gray-700 rounded-full focus:ring-2 focus:ring-[#22c55e] text-white bg-gray-900 text-sm"
                    />
                    <button onClick={handleSendMessage} disabled={(!newMessage.trim() && !grpMediaFile) || grpSendingMedia} className="p-3 bg-[#16a34a] text-pure-white rounded-full hover:bg-[#15803d] disabled:opacity-50 flex-shrink-0">
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Members Tab */}
            {activeTab === 'members' && (
              <div>
                <div className="space-y-3">
                  {memberUsers.map(user => {
                    const isSelf = user.id === currentUser.id;
                    const userIsOwner = user.id === group.createdBy;
                    const userIsModerator = group.moderators?.includes(user.id);
                    const canKick = isOwnerOrModerator && !isSelf && !userIsOwner && !(userIsModerator && !isOwner);
                    const canPromote = isOwner && !isSelf && !userIsOwner && !userIsModerator;
                    const canDemote = isOwner && userIsModerator;

                    return (
                      <div key={user.id} className="flex items-center justify-between p-2 hover:bg-gray-900 rounded-lg transition-colors">
                        <button onClick={() => { if (!isSelf) setView(`user-profile-${user.id}`); }} className="flex items-center space-x-3 text-left flex-1">
                          <img src={user.profilePicture} alt={user.name} className="w-10 h-10 rounded-full" />
                          <div>
                            <div className="flex items-center space-x-2">
                              <p className="text-white font-bold">{user.name}</p>
                              {userIsOwner && <span className="text-xs bg-yellow-600 text-pure-white px-2 py-0.5 rounded">Owner</span>}
                              {userIsModerator && !userIsOwner && <span className="text-xs bg-purple-600 text-pure-white px-2 py-0.5 rounded">Mod</span>}
                            </div>
                            <p className="text-sm text-gray-400">@{user.handle}</p>
                          </div>
                        </button>
                        <div className="flex space-x-1">
                          {canPromote && (
                            <button onClick={() => handleAddModerator(user.id)} className="p-1.5 text-purple-400 hover:text-purple-300 hover:bg-purple-900 hover:bg-opacity-30 rounded-lg" title="Make moderator">
                              <Shield className="w-4 h-4" />
                            </button>
                          )}
                          {canDemote && (
                            <button onClick={() => handleRemoveModerator(user.id)} className="p-1.5 text-gray-400 hover:text-gray-300 hover:bg-gray-700 rounded-lg" title="Remove moderator">
                              <Shield className="w-4 h-4" />
                            </button>
                          )}
                          {canKick && (
                            <button onClick={() => handleRemoveMember(user.id)} className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-900 hover:bg-opacity-30 rounded-lg" title="Remove member">
                              <UserX className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Join Requests — owner and moderators */}
                {isOwnerOrModerator && group.joinRequests?.length > 0 && (
                  <>
                    <h3 className="text-lg font-bold text-white mb-3 mt-6">Join Requests ({group.joinRequests.length})</h3>
                    <div className="space-y-3">
                      {group.joinRequests.map(userId => {
                        const reqUser = users.find(u => u.id === userId);
                        if (!reqUser) return null;
                        return (
                          <div key={userId} className="flex items-center justify-between p-2 bg-gray-900 rounded-lg border border-gray-700">
                            <button onClick={() => setView(`user-profile-${userId}`)} className="flex items-center space-x-3 text-left">
                              <img src={reqUser.profilePicture} alt={reqUser.name} className="w-10 h-10 rounded-full" />
                              <div>
                                <p className="text-white font-medium">{reqUser.name}</p>
                                <p className="text-sm text-gray-400">@{reqUser.handle}</p>
                              </div>
                            </button>
                            <div className="flex space-x-2">
                              <button onClick={() => handleAcceptJoinRequest(userId)} className="px-3 py-1.5 bg-[#16a34a] text-pure-white rounded-lg text-sm hover:bg-[#15803d]">Accept</button>
                              <button onClick={() => handleDeclineJoinRequest(userId)} className="px-3 py-1.5 bg-gray-700 text-white rounded-lg text-sm hover:bg-gray-600">Decline</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Pending Invites */}
                {isOwnerOrModerator && group.pendingInvites?.length > 0 && !isPendingInvite && (
                  <>
                    <h3 className="text-lg font-bold text-white mb-3 mt-6">Pending Invites ({group.pendingInvites.length})</h3>
                    <div className="space-y-3">
                      {group.pendingInvites.map(userId => {
                        const invitedUser = users.find(u => u.id === userId);
                        if (!invitedUser) return null;
                        return (
                          <div key={userId} className="flex items-center space-x-3 p-2 rounded-lg opacity-60">
                            <img src={invitedUser.profilePicture} alt={invitedUser.name} className="w-10 h-10 rounded-full" />
                            <div>
                              <p className="text-white font-medium">{invitedUser.name} <span className="text-xs text-yellow-500">(Pending)</span></p>
                              <p className="text-sm text-gray-400">@{invitedUser.handle}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Group Modal — owner only */}
      {showEditGroup && isOwner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-black border border-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">Edit Group</h3>
              <button onClick={() => setShowEditGroup(false)} className="text-white hover:text-gray-300"><X className="w-6 h-6" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">Group Name *</label>
                <input
                  type="text"
                  value={editGroupForm.name}
                  maxLength={25}
                  onChange={e => { const val = e.target.value.replace(/[^a-zA-Z0-9 ]/g, ''); setEditGroupForm({ ...editGroupForm, name: val }); }}
                  className="w-full px-3 py-2 border border-gray-700 rounded-lg text-white bg-black focus:ring-2 focus:ring-[#16a34a]"
                />
                <span className={`text-xs float-right mt-1 ${editGroupForm.name.length > 20 ? 'text-yellow-400' : 'text-gray-500'}`}>{editGroupForm.name.length}/25</span>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Description</label>
                <textarea
                  value={editGroupForm.description}
                  onChange={e => setEditGroupForm({ ...editGroupForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-700 rounded-lg text-white bg-black focus:ring-2 focus:ring-[#16a34a]"
                  rows="3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Privacy</label>
                <div className="flex space-x-2">
                  <button type="button" onClick={() => setEditGroupForm({ ...editGroupForm, isPrivate: false })} className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${!editGroupForm.isPrivate ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-800 text-gray-400 border border-gray-700 hover:text-white'}`}>Public</button>
                  <button type="button" onClick={() => setEditGroupForm({ ...editGroupForm, isPrivate: true })} className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-1 ${editGroupForm.isPrivate ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-800 text-gray-400 border border-gray-700 hover:text-white'}`}><Lock className="w-4 h-4" /><span>Private</span></button>
                </div>
              </div>
              <div className="flex space-x-3 pt-2">
                <button onClick={() => setShowEditGroup(false)} className="flex-1 px-3 py-2 border border-gray-700 text-white rounded-lg hover:bg-gray-900">Cancel</button>
                <button onClick={handleSaveEditGroup} disabled={!editGroupForm.name.trim() || savingGroupEdit} className="flex-1 px-3 py-2 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d] disabled:opacity-50">
                  {savingGroupEdit ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal — owner and moderators only */}
      {showInvite && isOwnerOrModerator && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-black border border-gray-800 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">Invite Members</h3>
              <button onClick={() => { setShowInvite(false); setSelectedMembers([]); }} className="text-white hover:text-gray-300"><X className="w-6 h-6" /></button>
            </div>
            <p className="text-sm text-gray-400 mb-3">Select users to invite to this group</p>
            <div className="space-y-2 mb-4">
              {invitableUsers.map(u => (
                <button key={u.id} onClick={() => setSelectedMembers(prev => prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id])} className={`flex items-center justify-between w-full p-2 rounded-lg transition-colors ${selectedMembers.includes(u.id) ? 'bg-[#16a34a] bg-opacity-20 border border-[#16a34a]' : 'hover:bg-gray-900 border border-transparent'}`}>
                  <div className="flex items-center space-x-3">
                    <img src={u.profilePicture} alt={u.name} className="w-10 h-10 rounded-full" />
                    <div className="text-left">
                      <p className="text-white font-medium text-sm">{u.name}</p>
                      <p className="text-xs text-gray-400">@{u.handle}</p>
                    </div>
                  </div>
                  {selectedMembers.includes(u.id) && <CheckCircle className="w-5 h-5 text-[#16a34a]" />}
                </button>
              ))}
              {invitableUsers.length === 0 && <p className="text-gray-500 text-center py-4 text-sm">No users available to invite. Follow more golfers!</p>}
            </div>
            {selectedMembers.length > 0 && (
              <button onClick={handleInviteMembers} className="w-full py-2 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d]">
                Invite {selectedMembers.length} Member{selectedMembers.length > 1 ? 's' : ''}
              </button>
            )}
          </div>
        </div>
      )}
      {grpViewMedia && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setGrpViewMedia(null)}>
          <button onClick={() => setGrpViewMedia(null)} className="absolute top-4 right-4 text-white hover:text-gray-300 z-50">
            <X className="w-8 h-8" />
          </button>
          <div onClick={(e) => e.stopPropagation()} className="max-w-full max-h-full">
            {grpViewMedia.type === 'image' ? (
              <img src={grpViewMedia.url} alt="" className="max-w-full max-h-[90vh] object-contain rounded-lg" />
            ) : (
              <video src={grpViewMedia.url} controls autoPlay playsInline className="max-w-full max-h-[90vh] rounded-lg" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== MY SCORECARDS ====================

export default GroupDetailView;
